/**
 * The games a place runs (place manager, unit 5): which games it can run,
 * which one a room starts on, for the Games card and the Games section. The
 * reads come in through {@link GamesApi}, so the join is unit-testable without
 * a network; the real reads live in `place-games-api.ts`.
 *
 * Which games: a brand's place runs its brand's game list, joined to the
 * catalog by slug. The list is opaque (a slug need not name a game in the
 * catalog), so a slug with no entry is shown as such ("not an active catalog
 * game" to staff, who still never read archived games), and a
 * listed game switched off is shown, never counted. An empty list means the
 * brand's app shows no games. A house place (no brand) runs every enabled game.
 *
 * What the rooms play right now is not here: it comes from the room-now read
 * (`playingNowOf` in `place-today.tsx`), never a second read.
 */
import type { GameCatalogData, GameData, PlaceGamesData } from "@/data/place-games-api";

/** The words for the two engines the brief names; any other value the
 *  backend carries is shown as it is, never given an invented word.
 *  `internal-server` (our gameserver fleet) was retired with gameservers:
 *  the backend no longer lets one be enabled, and a one-off cleanup job
 *  disables the rest. */
const ENGINE_WORDS: Record<string, string> = {
  "internal-server": "retired",
  "cloudflare-worker": "serverless",
};

/** A game's engine, from its `activityServerType`. */
export function engineLabel(serverType: string): string {
  return ENGINE_WORDS[serverType] ?? serverType;
}

/** A game as the section draws it. */
export interface Game {
  name: string;
  slug: string;
  /** {@link engineLabel}. */
  engine: string;
  enabled: boolean;
}

export function gameOf(entry: GameData): Game {
  return { name: entry.name, slug: entry.slug, engine: engineLabel(entry.activityServerType), enabled: entry.isEnabled };
}

/** Each catalog read's games by slug, joined once (a page re-renders often). */
const joined = new WeakMap<GameCatalogData, Map<string, Game>>();

/** The catalog's games by slug, in the catalog's order, each slug once: matched
 *  exactly, the first entry winning, as the brand's app matches them. The one
 *  join for the place's Games section and the brand's Games page (unit 8). */
export function catalogBySlug(catalog: GameCatalogData): ReadonlyMap<string, Game> {
  let bySlug = joined.get(catalog);
  if (!bySlug) {
    bySlug = new Map();
    for (const entry of catalog.nodes) if (!bySlug.has(entry.slug)) bySlug.set(entry.slug, gameOf(entry));
    joined.set(catalog, bySlug);
  }
  return bySlug;
}

/** What a listed slug with no catalog entry is called. Anyone but staff
 *  reads the enabled games only (`enabledOnly`), so for them a game switched
 *  off looks the same as one not in the catalog: said as such, never as
 *  missing. Staff read every game but the archived ones (the backend never
 *  lists those), so for them it is "not an active catalog game": it may name
 *  an archived game. Shared by the place's Games section and the brand's
 *  Games page (unit 8). */
export function unknownWords(enabledOnly: boolean): string {
  return enabledOnly ? "not among the enabled games" : "not an active catalog game";
}

/** One line of the list: a slug, and the catalog's game for it (null: the
 *  slug names no game in the catalog). */
export interface GameRow {
  slug: string;
  game: Game | null;
}

/** What a room starts on. */
export interface StartsOn {
  /** The game, after the platform's fallbacks; null when none resolves (the
   *  backend's sign of a misconfiguration, not a failed read). */
  game: Game | null;
  /** The place pins it itself, rather than taking the platform default. */
  pinned: boolean;
}

/** What the Games card and section draw for one place. */
export interface PlaceGames {
  /** "brand": runs its brand's game list; "house": no brand, every enabled game. */
  kind: "brand" | "house";
  /** The games it can run: the listed ones in the catalog and enabled (a
   *  house place: every enabled game). */
  count: number;
  /** Listed slugs that name no game in the catalog. */
  unknown: number;
  /** Listed games in the catalog but switched off (a house place: the
   *  catalog's games switched off). */
  disabled: number;
  /** A brand's place: its list, in the list's order, joined to the catalog.
   *  A house place: the catalog, in its own order. */
  rows: GameRow[];
  startsOn: { main: StartsOn; tables: StartsOn };
  /** The catalog read lists enabled games only (its `all` was false), so a
   *  game switched off cannot be told from a slug not in the catalog. */
  enabledOnly: boolean;
}

/** The two reads this section makes (`place-games-api.ts`, with the viewer
 *  bound). */
export interface GamesApi {
  /** The place's game settings; null when the place is not visible. */
  place(placeId: string): Promise<PlaceGamesData | null>;
  /** The catalog, saying which list it read (`all`). */
  catalog(): Promise<GameCatalogData>;
}

function startsOnOf(resolved: { activity: GameData } | null, pinned: { id: string } | null): StartsOn {
  return { game: resolved ? gameOf(resolved.activity) : null, pinned: pinned !== null };
}

/** The section's model from the two reads. Whether the catalog lists the
 *  enabled games only follows what it read (`catalog.all`). */
export function placeGamesOf(place: PlaceGamesData, catalog: GameCatalogData): PlaceGames {
  const enabledOnly = !catalog.all;
  const startsOn = {
    main: startsOnOf(place.resolvedRootGatheringDefaults, place.defaultActivityForRootGathering),
    tables: startsOnOf(place.resolvedSubGatheringDefaults, place.defaultActivityForSubGatherings),
  };
  if (!place.brand) {
    const games = catalog.nodes.map(gameOf);
    return {
      kind: "house",
      count: games.filter((g) => g.enabled).length,
      unknown: 0,
      disabled: games.filter((g) => !g.enabled).length,
      rows: games.map((game) => ({ slug: game.slug, game })),
      startsOn,
      enabledOnly,
    };
  }
  const bySlug = catalogBySlug(catalog);
  const rows = place.brand.allowedGameSlugs.map((slug) => ({ slug, game: bySlug.get(slug) ?? null }));
  return {
    kind: "brand",
    count: rows.filter((r) => r.game?.enabled).length,
    unknown: rows.filter((r) => !r.game).length,
    disabled: rows.filter((r) => r.game && !r.game.enabled).length,
    rows,
    startsOn,
    enabledOnly,
  };
}

/** The two reads: the place's, then the catalog only when the place is
 *  visible (null when it is not; no catalog read is made). Which catalog list
 *  is read (staff: every game; anyone else: the enabled ones) is the api's
 *  business; the model follows what it read. */
export async function readPlaceGames(placeId: string, api: GamesApi): Promise<PlaceGames | null> {
  const place = await api.place(placeId);
  return place ? placeGamesOf(place, await api.catalog()) : null;
}
